import React from 'react';
import { FaSortAmountDown, FaSortAmountUp, FaTimes } from 'react-icons/fa';

/**
 * SortControl - Componente para controlar el ordenamiento por campos adicionales (metadata)
 *
 * @param {Object} sortConfig - Configuración actual de ordenamiento { field, order }
 * @param {Function} onSortChange - Callback al cambiar el ordenamiento
 * @param {Array} metadataSpecs - Especificaciones de metadata para poblar las opciones
 * @param {Array} systemFields - Campos del sistema ordenables, como `{ key, label }`. Los
 *   decide la pantalla que usa el buscador, no este componente: en Control de Calidad dependen
 *   del estado de la pestaña (issue UBA #43). Antes estaban hardcodeados acá, y por eso se
 *   podía ordenar por "Fecha de firma" en pestañas donde esa columna es nula en todas las filas.
 */
const SortControl = ({ sortConfig = [], onSortChange, metadataSpecs = [], systemFields = [] }) => {

    // Obtener campos de metadata que son "sortable" según la spec (o todos si no hay flag específico aún)
    // Asumimos que todos los campos de metadata pueden ser candidatos para ordenar
    const sortableFields = metadataSpecs.map(spec => ({
        key: `metadata.${spec.name}`,
        label: spec.label || spec.name
    }));

    const allOptions = [...systemFields, ...sortableFields];

    // Manejar cambio en el select de campo
    const handleFieldChange = (e) => {
        const field = e.target.value;
        if (!field) return;

        // Si ya existe, mantener orden, sino default ASC
        const existing = sortConfig.find(s => s.field === field);
        const order = existing ? existing.order : 'asc';

        // Reemplazar o agregar? 
        // Para simplificar UX inicial: Reemplaza el sort actual (Single Column Sort priority)
        // pero permite mantener si el usuario quiere. 
        // El requerimiento dice "elegir campos". Vamos a permitir agregar.

        // Estrategia: Si selecciono un campo, lo pongo como PREFERENTE (primero)
        const newSort = [
            { field, order },
            ...sortConfig.filter(s => s.field !== field)
        ];
        onSortChange(newSort);
    };

    // Manejar cambio de dirección para un campo específico
    const handleDirectionToggle = (field) => {
        const newSort = sortConfig.map(s => {
            if (s.field === field) {
                return { ...s, order: s.order === 'asc' ? 'desc' : 'asc' };
            }
            return s;
        });
        onSortChange(newSort);
    };

    // Eliminar un criterio de ordenamiento
    const handleRemoveSort = (field) => {
        const newSort = sortConfig.filter(s => s.field !== field);
        onSortChange(newSort);
    };

    // Encontrar etiqueta para mostrar
    const getLabel = (fieldKey) => {
        const option = allOptions.find(o => o.key === fieldKey);
        return option ? option.label : fieldKey;
    };

    return (
        <div style={styles.container}>
            <div style={styles.selectorContainer}>
                <span style={styles.label}>Ordenar por:</span>
                <select
                    style={styles.select}
                    onChange={handleFieldChange}
                    value="" // Siempre resetear para permitir volver a seleccionar el mismo si fuera necesario (aunque no aplica mucho aquí)
                >
                    <option value="" disabled>Seleccionar campo...</option>
                    {allOptions.map(opt => (
                        <option key={opt.key} value={opt.key}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            </div>

            <div style={styles.tagsContainer}>
                {sortConfig.map((sort, index) => (
                    <div key={sort.field} style={styles.sortTag}>
                        <span style={styles.tagName}>{getLabel(sort.field)}</span>

                        <button
                            onClick={() => handleDirectionToggle(sort.field)}
                            style={styles.directionButton}
                            title={sort.order === 'asc' ? 'Ascendente' : 'Descendente'}
                        >
                            {sort.order === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
                        </button>

                        <button
                            onClick={() => handleRemoveSort(sort.field)}
                            style={styles.removeButton}
                            title="Quitar ordenamiento"
                        >
                            <FaTimes />
                        </button>
                        {index < sortConfig.length - 1 && <span style={styles.priorityArrow}>→</span>}
                    </div>
                ))}
            </div>
        </div>
    );
};

const styles = {
    container: {
        marginBottom: '10px',
        padding: '10px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '15px'
    },
    selectorContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    label: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#495057'
    },
    select: {
        padding: '6px 12px',
        borderRadius: '4px',
        border: '1px solid #ced4da',
        fontSize: '14px',
        color: '#495057',
        cursor: 'pointer',
        backgroundColor: '#fff',
    },
    tagsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        alignItems: 'center'
    },
    sortTag: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#fff',
        border: '1px solid #dcdcdc',
        borderRadius: '20px',
        padding: '4px 8px 4px 12px',
        fontSize: '13px',
        color: '#00468B',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
    },
    tagName: {
        fontWeight: '500',
        marginRight: '8px'
    },
    directionButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#00468B',
        display: 'flex',
        alignItems: 'center',
        padding: '4px',
        borderRadius: '50%',
        transition: 'background 0.2s',
        ':hover': {
            backgroundColor: '#f1f3f5'
        }
    },
    removeButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#adb5bd',
        display: 'flex',
        alignItems: 'center',
        padding: '4px',
        marginLeft: '4px',
        transition: 'color 0.2s',
        ':hover': {
            color: '#dc3545'
        }
    },
    priorityArrow: {
        color: '#adb5bd',
        fontSize: '12px',
        fontWeight: 'bold',
        marginLeft: '10px'
    }
};

export default SortControl;
